import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, UserPlus, LogOut, Trash2, ShieldOff, Loader2,
  CheckCircle2, MailCheck, ClockAlert,
} from "lucide-react";
import {
  portalListUsers, portalListInvitations, portalCreateInvitation,
  portalRevokeInvitation, portalSuspendUser,
  clearCommunityPartnerToken, getCommunityPartnerToken,
  formatCommunityPartnerError,
  ORG_ADMIN_ASSIGNABLE_ROLES,
} from "@/services/communityPartnerApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

/**
 * HC-EPIC-004 · Sprint 1-B · Community Partner Portal · Users screen.
 *
 * Route: `/community-partner-portal/users`
 *
 * Owner directive §6D:
 *   - Active org admins may view users + pending invitations for their
 *     own org (server-derived scope; no organization picker exposed).
 *   - Invite an ordinary or read-only user only. The allowlist of
 *     assignable roles is imported from the API client as a fixed
 *     constant — no arbitrary role editing.
 *   - Revoke a pending invitation.
 *   - Suspend an ordinary user where permitted.
 *   - Self-suspend and sibling-admin suspend are blocked server-side;
 *     the UI hides the button on those rows as a secondary defence
 *     (backend remains authoritative).
 */
export default function CommunityPartnerPortalUsers() {
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notEnabled, setNotEnabled] = useState(false);
  const [needsAuth, setNeedsAuth] = useState(false);
  const [err, setErr] = useState("");

  // Invite form
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [roleKey, setRoleKey] = useState(ORG_ADMIN_ASSIGNABLE_ROLES[0].key);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [lastInviteResult, setLastInviteResult] = useState(null);

  const refresh = useCallback(async () => {
    setErr("");
    if (!getCommunityPartnerToken()) {
      setNeedsAuth(true);
      setLoading(false);
      return;
    }
    setNeedsAuth(false);
    try {
      const [u, i] = await Promise.all([portalListUsers(), portalListInvitations()]);
      setUsers(u.items || []);
      setInvitations(i.items || []);
      setNotEnabled(false);
    } catch (e) {
      const s = e?.response?.status;
      if (s === 404) {
        // Backend flag disabled or no such route. Show a friendly
        // "not enabled" state so nothing in the UI implies the feature
        // is available.
        setNotEnabled(true);
      } else if (s === 401 || s === 403) {
        setNeedsAuth(true);
      } else {
        setErr(formatCommunityPartnerError(e, "Unable to load your portal."));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const logout = () => {
    clearCommunityPartnerToken();
    nav("/community-partner-portal/activate", { replace: true });
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    if (inviteBusy) return;
    setInviteBusy(true);
    setLastInviteResult(null);
    try {
      const data = await portalCreateInvitation({ email, roleKey, displayName });
      setLastInviteResult(data);
      setEmail("");
      setDisplayName("");
      toast.success("Invitation created.");
      refresh();
    } catch (e) {
      const msg = formatCommunityPartnerError(e, "Unable to create invitation.");
      toast.error(msg);
    } finally {
      setInviteBusy(false);
    }
  };

  const revokeInvite = async (invitationId) => {
    try {
      await portalRevokeInvitation(invitationId);
      toast.success("Invitation revoked.");
      refresh();
    } catch (e) {
      toast.error(formatCommunityPartnerError(e, "Unable to revoke invitation."));
    }
  };

  const suspendUser = async (userId) => {
    try {
      await portalSuspendUser(userId);
      toast.success("User suspended.");
      refresh();
    } catch (e) {
      toast.error(formatCommunityPartnerError(e, "Unable to suspend user."));
    }
  };

  const activeCount = useMemo(
    () => users.filter((u) => u.status === "Active").length,
    [users]
  );
  const pendingInviteCount = useMemo(
    () => invitations.filter((i) => i.state === "pending" || i.state === "harborwell_review").length,
    [invitations]
  );

  if (loading) {
    return (
      <div className="p-8 text-slate-500 flex items-center gap-2"
           data-testid="cp-portal-users-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (notEnabled) {
    return (
      <div className="p-8 max-w-lg mx-auto" data-testid="cp-portal-users-not-enabled">
        <Alert>
          <AlertDescription>
            The Community Partner Portal is not currently enabled for this environment.
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  if (needsAuth) {
    return (
      <div className="p-8 max-w-lg mx-auto" data-testid="cp-portal-users-needs-auth">
        <Alert variant="destructive">
          <AlertDescription>
            Your session has ended. Please activate your account again from the
            invitation link you were sent.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-slate-900 text-white p-2">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-slate-900"
                  data-testid="cp-portal-users-title">
                Community Partner Portal · Users
              </h1>
              <p className="text-sm text-slate-500">
                Manage the members of your organization.
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={logout} data-testid="cp-portal-users-logout">
            <LogOut className="w-4 h-4 mr-1" /> Sign out
          </Button>
        </div>

        {err && (
          <Alert variant="destructive" data-testid="cp-portal-users-error">
            <AlertDescription>{err}</AlertDescription>
          </Alert>
        )}

        {/* Active-user count vs limit */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6"
             data-testid="cp-portal-users-counts">
          <div className="flex flex-wrap items-baseline gap-6">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Active users</div>
              <div className="text-2xl font-semibold text-slate-900"
                   data-testid="cp-portal-users-active-count">{activeCount}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Pending invitations</div>
              <div className="text-2xl font-semibold text-slate-900"
                   data-testid="cp-portal-users-pending-count">{pendingInviteCount}</div>
            </div>
          </div>
        </div>

        {/* Invite form */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6"
             data-testid="cp-portal-users-invite-card">
          <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <UserPlus className="w-4 h-4" /> Invite a new member
          </h2>
          <form onSubmit={submitInvite} className="grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="cp-invite-email">Email</Label>
              <Input id="cp-invite-email" type="email" required
                     data-testid="cp-portal-invite-email-input"
                     value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="cp-invite-name">Display name (optional)</Label>
              <Input id="cp-invite-name" type="text"
                     data-testid="cp-portal-invite-name-input"
                     value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="cp-invite-role">Role</Label>
              <Select value={roleKey} onValueChange={setRoleKey}>
                <SelectTrigger id="cp-invite-role" data-testid="cp-portal-invite-role-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ORG_ADMIN_ASSIGNABLE_ROLES.map((r) => (
                    <SelectItem key={r.key} value={r.key}
                                data-testid={`cp-portal-invite-role-${r.key}`}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Only ordinary and read-only roles can be invited from here.
                Contact HarborWell to designate a new organization administrator.
              </p>
            </div>
            <div className="flex items-end">
              <Button type="submit" disabled={inviteBusy}
                      data-testid="cp-portal-invite-submit"
                      className="w-full md:w-auto">
                {inviteBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send invitation"}
              </Button>
            </div>
          </form>
          {lastInviteResult && (
            <div className="mt-4 text-sm text-emerald-700 flex items-center gap-2"
                 data-testid="cp-portal-invite-result">
              <MailCheck className="w-4 h-4" />
              {lastInviteResult.state === "harborwell_review"
                ? "Invitation submitted for HarborWell review."
                : "Invitation created and ready to share with the invitee."}
            </div>
          )}
        </div>

        {/* Pending invitations */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6"
             data-testid="cp-portal-users-invitations-card">
          <h2 className="font-semibold text-slate-900 mb-4">Pending invitations</h2>
          {invitations.length === 0 ? (
            <div className="text-sm text-slate-500">No pending invitations.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-2">Email</th>
                  <th className="py-2">Role</th>
                  <th className="py-2">Status</th>
                  <th className="py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="cp-portal-invitations-tbody">
                {invitations.map((i) => (
                  <tr key={i.id} className="border-t border-slate-100">
                    <td className="py-2">{i.email}</td>
                    <td className="py-2">
                      {ORG_ADMIN_ASSIGNABLE_ROLES.find((r) => r.key === i.role_key)?.label || i.role_key}
                    </td>
                    <td className="py-2">
                      <Badge variant={i.state === "harborwell_review" ? "secondary" : "outline"}>
                        {i.state === "harborwell_review" ? "Awaiting HarborWell" : "Pending"}
                      </Badge>
                    </td>
                    <td className="py-2 text-right">
                      {(i.state === "pending" || i.state === "harborwell_review") && (
                        <Button size="sm" variant="ghost"
                                onClick={() => revokeInvite(i.id)}
                                data-testid={`cp-portal-invite-revoke-${i.id}`}>
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Users */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6"
             data-testid="cp-portal-users-list-card">
          <h2 className="font-semibold text-slate-900 mb-4">Members</h2>
          {users.length === 0 ? (
            <div className="text-sm text-slate-500">No members yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-2">Email</th>
                  <th className="py-2">Role</th>
                  <th className="py-2">Status</th>
                  <th className="py-2">MFA</th>
                  <th className="py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="cp-portal-users-tbody">
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-slate-100"
                      data-testid={`cp-portal-user-row-${u.id}`}>
                    <td className="py-2">{u.email}</td>
                    <td className="py-2">
                      {ORG_ADMIN_ASSIGNABLE_ROLES.find((r) => r.key === u.role_key)?.label
                        || (u.role_key === "community_partner_organization_admin"
                              ? "Organization Administrator"
                              : u.role_key)}
                    </td>
                    <td className="py-2">
                      <StatusBadge status={u.status} />
                    </td>
                    <td className="py-2">
                      {u.mfa_enrolled
                        ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Enrolled</span>
                        : <span className="inline-flex items-center gap-1 text-amber-700"><ClockAlert className="w-3.5 h-3.5" /> Pending</span>}
                    </td>
                    <td className="py-2 text-right">
                      {u.status === "Active"
                        && u.role_key !== "community_partner_organization_admin" && (
                        <Button size="sm" variant="ghost"
                                onClick={() => suspendUser(u.id)}
                                data-testid={`cp-portal-user-suspend-${u.id}`}>
                          <ShieldOff className="w-3.5 h-3.5 mr-1" /> Suspend
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const variant = status === "Active" ? "default"
                : status === "Invited" ? "secondary"
                : "outline";
  return <Badge variant={variant} data-testid={`cp-portal-status-badge-${status}`}>{status}</Badge>;
}
